import * as XLSX from 'xlsx';
import { getLicenceTypeLabel, isAssigned } from './assignmentModel';

export const EMAIL_HEADER = 'Work email';
export const ASSIGNMENTS_SHEET = 'Assignments';
export const INSTRUCTIONS_SHEET = 'Instructions';
export const META_SHEET = '_meta';

const EMAIL_HEADERS = ['work email', 'email', 'email address'];
const IGNORED_HEADERS = ['first name', 'last name', 'surname', 'name', 'full name'];

export const ISSUE_LABELS = {
    unknown_email: 'Unknown email',
    user_not_in_project: 'User not in project',
    duplicate_email: 'Duplicate email',
    unknown_column: 'Unknown column',
    invalid_value: 'Invalid value',
    exceeds_allocation: 'Exceeds allocation',
    missing_email: 'Missing email',
};

const normalizeHeader = value => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

export const getLicenceHeader = licence => `${getLicenceTypeLabel(licence.type)}: ${licence.name}`;

export const buildLicenceHeaders = (licences) => {
    const used = {};
    return licences.map((licence) => {
        const base = getLicenceHeader(licence);
        const normalized = normalizeHeader(base);
        used[normalized] = (used[normalized] || 0) + 1;
        return {
            key: licence.key,
            header: used[normalized] > 1 ? `${base} (${used[normalized]})` : base,
        };
    });
};

export const getTemplateFilename = (projectName, fileFormat = 'xlsx') => {
    const slug = String(projectName || 'project')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return `${slug || 'project'}-licence-assignment.${fileFormat === 'csv' ? 'csv' : 'xlsx'}`;
};

export const buildTemplateWorkbook = ({ projectId, users, licences, assignments }) => {
    const headers = buildLicenceHeaders(licences);
    const rows = [
        [EMAIL_HEADER, 'First name', 'Last name', ...headers.map(item => item.header)],
        ...users.map(user => [
            user.email,
            user.first_name,
            user.last_name,
            ...headers.map(item => (isAssigned(assignments, item.key, user.uuid) ? 'Y' : 'N')),
        ]),
    ];
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet['!cols'] = [
        { wch: 34 },
        { wch: 16 },
        { wch: 16 },
        ...headers.map(item => ({ wch: Math.min(40, Math.max(12, item.header.length + 2)) })),
    ];

    const instructions = XLSX.utils.aoa_to_sheet([
        ['How to use this template'],
        ['Y assigns the licence to the user.'],
        ['N removes the licence from the user.'],
        ['Leave a cell blank to keep it unchanged.'],
        ['Users are matched on work email. Only users already added to this project can be assigned.'],
        ['Assignments that exceed the project allocation are flagged for review and not applied.'],
        ['Do not rename the licence columns or the hidden _meta sheet.'],
    ]);
    instructions['!cols'] = [{ wch: 96 }];

    const meta = XLSX.utils.aoa_to_sheet([
        ['header', 'licence_key'],
        ...headers.map(item => [item.header, item.key]),
        ['project_uuid', projectId || ''],
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, ASSIGNMENTS_SHEET);
    XLSX.utils.book_append_sheet(workbook, instructions, INSTRUCTIONS_SHEET);
    XLSX.utils.book_append_sheet(workbook, meta, META_SHEET);
    workbook.Workbook = { Sheets: [{ Hidden: 0 }, { Hidden: 0 }, { Hidden: 1 }] };
    return workbook;
};

export const buildTemplateCsv = (templateArgs) => {
    const workbook = buildTemplateWorkbook(templateArgs);
    return XLSX.utils.sheet_to_csv(workbook.Sheets[ASSIGNMENTS_SHEET]);
};

export const readWorkbookRows = (data) => {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames.includes(ASSIGNMENTS_SHEET)
        ? ASSIGNMENTS_SHEET
        : workbook.SheetNames.find(name => ![INSTRUCTIONS_SHEET, META_SHEET].includes(name)) || workbook.SheetNames[0];
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
        defval: '',
        raw: false,
        blankrows: false,
    });

    let meta = null;
    const metaSheet = workbook.Sheets[META_SHEET];
    if (metaSheet) {
        meta = {};
        XLSX.utils.sheet_to_json(metaSheet, { header: 1, defval: '', raw: false }).slice(1).forEach(([header, key]) => {
            if (header && key && header !== 'project_uuid') meta[normalizeHeader(header)] = String(key);
        });
    }
    return { rows, meta };
};

export const normalizeCellValue = (value) => {
    const text = String(value ?? '').trim().toUpperCase();
    if (!text) return null;
    if (['Y', 'YES', '1', 'TRUE'].includes(text)) return true;
    if (['N', 'NO', '0', 'FALSE'].includes(text)) return false;
    return 'invalid';
};

const isBlankRow = row => !row || row.every(cell => String(cell ?? '').trim() === '');

/**
 * Mirrors the backend preview contract: Y assigns, N removes, blank is unchanged.
 * Removals free capacity before additions are checked, in file order.
 */
export const buildUploadPreview = ({ rows, meta, users, licences, baseline }) => {
    const [headerRow = [], ...dataRows] = rows || [];
    const emailIndex = headerRow.findIndex(header => EMAIL_HEADERS.includes(normalizeHeader(header)));
    if (emailIndex < 0) {
        return { error: `The file needs a "${EMAIL_HEADER}" column in the first row.` };
    }

    const licenceByKey = licences.reduce((items, licence) => {
        items[licence.key] = licence;
        return items;
    }, {});
    const headerToKey = {};
    buildLicenceHeaders(licences).forEach(({ key, header }) => {
        headerToKey[normalizeHeader(header)] = key;
    });
    licences.forEach((licence) => {
        const header = normalizeHeader(getLicenceHeader(licence));
        if (!headerToKey[header]) headerToKey[header] = licence.key;
    });
    Object.entries(meta || {}).forEach(([header, key]) => {
        headerToKey[header] = key;
    });

    const issues = [];
    const columns = [];
    headerRow.forEach((header, index) => {
        const normalized = normalizeHeader(header);
        if (index === emailIndex || !normalized || IGNORED_HEADERS.includes(normalized)) return;
        const key = headerToKey[normalized];
        if (!key || !licenceByKey[key]) {
            issues.push({
                row: 1,
                email: '',
                column: String(header),
                code: 'unknown_column',
                message: 'This column does not match a licence allocated to this project.',
            });
            return;
        }
        columns.push({ index, key, header: String(header) });
    });

    const userByEmail = users.reduce((items, user) => {
        if (user.email) items[user.email.trim().toLowerCase()] = user;
        return items;
    }, {});
    const seen = new Set();
    const intents = [];
    let rowsTotal = 0;
    let rowsMatched = 0;

    dataRows.forEach((row, rowIndex) => {
        if (isBlankRow(row)) return;
        const rowNumber = rowIndex + 2;
        rowsTotal += 1;
        const rawEmail = String(row[emailIndex] ?? '').trim();
        const email = rawEmail.toLowerCase();
        if (!email) {
            issues.push({ row: rowNumber, email: '', column: EMAIL_HEADER, code: 'missing_email', message: 'Work email is empty.' });
            return;
        }
        if (seen.has(email)) {
            issues.push({ row: rowNumber, email: rawEmail, column: EMAIL_HEADER, code: 'duplicate_email', message: 'This email appears more than once; only the first row is used.' });
            return;
        }
        seen.add(email);
        const user = userByEmail[email];
        if (!user) {
            issues.push({ row: rowNumber, email: rawEmail, column: EMAIL_HEADER, code: 'unknown_email', message: 'No user with this work email has been added to this project.' });
            return;
        }
        rowsMatched += 1;
        columns.forEach((column) => {
            const value = normalizeCellValue(row[column.index]);
            if (value === null) return;
            if (value === 'invalid') {
                issues.push({ row: rowNumber, email: rawEmail, column: column.header, code: 'invalid_value', message: `"${row[column.index]}" is not Y or N.` });
                return;
            }
            intents.push({ row: rowNumber, email: rawEmail, column: column.header, key: column.key, userId: user.uuid, assign: value });
        });
    });

    const working = {};
    const remaining = {};
    const perLicence = {};
    licences.forEach((licence) => {
        working[licence.key] = new Set(baseline?.[licence.key] || []);
        remaining[licence.key] = licence.remaining;
        perLicence[licence.key] = { name: licence.name, type: licence.type, add: [], remove: [], flagged: 0 };
    });

    intents.filter(intent => !intent.assign).forEach(({ key, userId }) => {
        if (working[key].delete(userId)) {
            remaining[key] += 1;
            perLicence[key].remove.push(userId);
        }
    });

    const flaggedRows = new Set();
    intents.filter(intent => intent.assign).forEach(({ row, email, column, key, userId }) => {
        if (working[key].has(userId)) return;
        if (remaining[key] <= 0) {
            flaggedRows.add(row);
            perLicence[key].flagged += 1;
            issues.push({ row, email, column, code: 'exceeds_allocation', message: 'No licences left in the project allocation.' });
            return;
        }
        working[key].add(userId);
        remaining[key] -= 1;
        perLicence[key].add.push(userId);
    });

    const add = [];
    const remove = [];
    const perLicenceRows = [];
    licences.forEach(({ key }) => {
        const item = perLicence[key];
        if (item.add.length) add.push({ licence_key: key, user_uuids: item.add });
        if (item.remove.length) remove.push({ licence_key: key, user_uuids: item.remove });
        if (item.add.length || item.remove.length || item.flagged) {
            perLicenceRows.push({
                licence_key: key,
                name: item.name,
                type: item.type,
                to_add: item.add.length,
                to_remove: item.remove.length,
                remaining_after: remaining[key],
                flagged: item.flagged,
            });
        }
    });
    const addedCount = add.reduce((total, item) => total + item.user_uuids.length, 0);
    const removedCount = remove.reduce((total, item) => total + item.user_uuids.length, 0);

    issues.sort((a, b) => a.row - b.row);

    return {
        summary: {
            rows_total: rowsTotal,
            rows_matched: rowsMatched,
            to_add: addedCount,
            to_remove: removedCount,
            rows_flagged: flaggedRows.size,
        },
        per_licence: perLicenceRows,
        issues,
        diff: { add, remove, addedCount, removedCount },
    };
};
